import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { ExternalLink, Check, X, ShieldOff } from "lucide-react";
import { Button } from "../ui/button";

export function VenueOrganizersQueue() {
  const pending = useQuery(api.venueOrganizers.getPendingVenueAuthorizations);
  const review = useMutation(api.venueOrganizers.reviewVenueAuthorization);
  const revoke = useMutation(api.venueOrganizers.revokeVenueAuthorization);

  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const handleAction = async (
    recordId: Id<"venueOrganizers">,
    action: "approve" | "reject"
  ) => {
    setBusy(true);
    try {
      await review({ recordId, action, reviewNotes: reviewNotes.trim() || undefined });
      setReviewingId(null);
      setReviewNotes("");
    } finally {
      setBusy(false);
    }
  };

  if (pending === undefined) {
    return <div className="py-8 text-center text-muted-foreground text-sm">Loading…</div>;
  }

  if (pending.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        No pending venue access requests.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {pending.map((r) => {
        const isOpen = reviewingId === r._id;
        return (
          <div key={r._id} className="border border-border rounded-lg p-4 space-y-3">
            {/* Header row */}
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-medium text-foreground">
                  {r.organizer?.name ?? r.organizer?.email ?? "Unknown organizer"}
                </p>
                <p className="text-xs text-muted-foreground">{r.organizer?.email}</p>
              </div>
              <span className="text-xs bg-yellow-500/10 text-yellow-400 px-2 py-0.5 rounded-full shrink-0">
                Pending
              </span>
            </div>

            {/* Venue */}
            <div className="text-sm">
              <span className="text-muted-foreground">Venue: </span>
              <span className="font-medium text-foreground">{r.venue?.name ?? "Unknown"}</span>
              {r.venue?.address && (
                <span className="text-muted-foreground"> · {r.venue.address}</span>
              )}
            </div>

            {/* Notes */}
            {r.notes && (
              <div className="text-sm">
                <span className="text-muted-foreground">Notes: </span>
                <span className="text-foreground">{r.notes}</span>
              </div>
            )}

            {/* Evidence link */}
            {r.evidenceUrl && (
              <a
                href={r.evidenceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                View evidence
              </a>
            )}

            {/* Review controls */}
            {isOpen ? (
              <div className="space-y-2 pt-1">
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Review notes (optional)"
                  rows={2}
                  className="w-full text-sm rounded-md border border-border bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleAction(r._id, "approve")}
                    disabled={busy}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Check className="w-3.5 h-3.5 mr-1" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleAction(r._id, "reject")}
                    disabled={busy}
                  >
                    <X className="w-3.5 h-3.5 mr-1" /> Reject
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setReviewingId(null); setReviewNotes(""); }}
                    disabled={busy}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setReviewingId(r._id)}
              >
                Review
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
